import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionPreferencesKeys } from '@data/constants';
import {
  SubscriptionPreferenceView,
  SubscriptionPreferencesBody,
  SubscriptionPreferencesListView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { APIError } from '@data/utils/types';

export const useSubscriptionPreferences = (subscription: string) =>
  useQuery<SubscriptionPreferencesListView, Error>({
    queryKey: subscriptionPreferencesKeys.list(subscription),
    queryFn: async () => {
      const response = await MatchingApi.preferences.getSubscriptionPreferences({
        subscriptionId: subscription,
      });
      return response.data;
    },
    enabled: !!subscription,
  });

export const useAddSubscriptionPreferences = (): UseMutationResult<
  SubscriptionPreferenceView,
  any,
  {
    key: string;
    subscription: string; // for 'volunteer' = 'patient', for 'trialManager' = trialId (NCT...)
    values: SubscriptionPreferencesBody;
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, subscription, values }) => {
      const response = await MatchingApi.preferences.updateSubscriptionPreferences({
        key,
        subscriptionId: subscription,
        subscriptionPreferencesBody: values,
      });
      return response.data;
    },
    onMutate: async ({ key, subscription, values }) => {
      await queryClient.cancelQueries({ queryKey: subscriptionPreferencesKeys.list(subscription) });

      const previousPreferences = queryClient.getQueryData<SubscriptionPreferencesListView>(
        subscriptionPreferencesKeys.list(subscription),
      );

      if (previousPreferences) {
        const newPages = previousPreferences.items.map((preference) =>
          preference.key === key
            ? {
                ...preference,
                preference: values,
              }
            : preference,
        );

        queryClient.setQueryData(subscriptionPreferencesKeys.list(subscription), {
          ...previousPreferences,
          items: newPages,
        });
      }

      return { previousPreferences };
    },
    onError: (_, { subscription }, context) =>
      queryClient.setQueryData(
        subscriptionPreferencesKeys.list(subscription),
        context?.previousPreferences,
      ),
  });
};

export const useUnsubscribeByToken = (): UseMutationResult<void, APIError, { token: string }> =>
  useMutation({
    mutationFn: async ({ token }) => {
      await MatchingApi.preferences.unsubscribeByToken({ unsubscribeByTokenBody: { token } });
    },
  });
